// An expanded row's child rows: rows of the same shape, loaded when the row first opens and drawn in the
// table's own columns, so they line up under the parent's cells and need no header of their own. Each
// loaded list is kept for the life of the table, so closing and reopening a row does not reload it. While a
// list loads, fails or comes back empty, one row spanning every column says so.

import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { useTable } from '../context'
import { BodyCell } from './BodyRow'

const stateFor = (cache, id) => cache.get(id) || { status: 'loading', rows: [] }

/** Loads one parent's child rows into the table's cache and reports where the load stands. A row that
 * remounts while its load is in flight waits on that load instead of starting another. */
const useChildRows = (row, id) => {
  const { childRows, childCache } = useTable()
  const [state, setState] = useState(() => stateFor(childCache, id))

  const watch = useCallback(pending => {
    let live = true
    pending.then(next => {
      if (live) setState(next)
    })
    return () => {
      live = false
    }
  }, [])

  const load = useCallback(() => {
    const pending = Promise.resolve()
      .then(() => childRows.load(row))
      .then(rows => ({ status: 'ready', rows: rows || [] }))
      .catch(() => ({ status: 'error', rows: [] }))
      .then(next => {
        // a retry started since owns the entry now
        if (childCache.get(id)?.pending === pending) childCache.set(id, next)
        return next
      })
    childCache.set(id, { status: 'loading', rows: [], pending })
    setState({ status: 'loading', rows: [] })
    return watch(pending)
  }, [childRows, childCache, row, id, watch])

  useEffect(() => {
    const cached = childCache.get(id)
    if (cached?.status === 'ready') return undefined
    if (cached?.pending) return watch(cached.pending)
    return load()
  }, [])

  return { ...state, retry: load }
}

const StatusRow = ({ id, children }) => {
  const { layout } = useTable()
  return (
    <tr className="me-table-child-status" id={id}>
      <td colSpan={layout.columnCount}>{children}</td>
    </tr>
  )
}

StatusRow.propTypes = {
  id: PropTypes.string.isRequired,
  children: PropTypes.node,
}

export const ChildRows = ({ id, row }) => {
  const { config, layout, childRows } = useTable()
  const parentId = String(row[config.rowKey])
  const { status, rows, retry } = useChildRows(row, parentId)
  const noun = childRows.noun || 'rows'

  if (status === 'loading')
    return (
      <StatusRow id={id}>
        <span className="me-table-child-loading" role="status">
          {`Loading ${noun}`}
        </span>
      </StatusRow>
    )
  if (status === 'error')
    return (
      <StatusRow id={id}>
        <span role="alert">{`Could not load ${noun}.`}</span>{' '}
        <button
          type="button"
          className="me-btn me-btn-secondary me-btn-sm"
          onClick={retry}
        >
          Retry
        </button>
      </StatusRow>
    )
  if (!rows.length)
    return (
      <StatusRow id={id}>
        {childRows.emptyText || `No ${noun} to show`}
      </StatusRow>
    )

  return rows.map((child, index) => (
    <tr
      key={String(child[config.rowKey])}
      id={index === 0 ? id : undefined}
      className="me-table-row is-child"
    >
      {layout.selectable && <td className="me-table-control" />}
      {layout.expandable && <td className="me-table-control" />}
      {layout.columns.map(column => (
        <BodyCell key={column.key} column={column} row={child} />
      ))}
      {layout.hasActions && <td className="me-table-actions" />}
    </tr>
  ))
}

ChildRows.propTypes = {
  // the parent's chevron controls this id: the first child row, or the status row in its place
  id: PropTypes.string.isRequired,
  row: PropTypes.object.isRequired,
}
